import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, Field, Notice } from '../components/ui';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post<void>('/auth/password-reset', { email });
      setSent(true);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="centred">
      <form className="card form" onSubmit={onSubmit}>
        <h1>Forgotten password</h1>

        {sent ? (
          // The server answers identically whether or not the address is known,
          // so this message has to as well. Saying "no such member" here would
          // undo that and turn the form into a membership list.
          <>
            <Notice kind="success">
              If that address belongs to a member account, a reset link is on its way. The
              link works once and expires in an hour.
            </Notice>
            <p className="muted small">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <>
            <p className="muted">
              Enter your email address and we will send you a link to choose a new password.
            </p>
            <ErrorNotice error={error} />
            <Field
              label="Email address"
              type="email"
              name="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <p className="muted small">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </form>
    </main>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [mismatch, setMismatch] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMismatch(false);

    if (password !== confirmation) {
      setMismatch(true);
      return;
    }

    setBusy(true);
    try {
      await api.post<void>('/auth/password-reset/complete', { token, password });
      // Completing a reset destroys the session, so the token this page was
      // holding is dead. Get a fresh one before the login form needs it.
      await api.refreshCsrfToken();
      navigate('/login', { replace: true, state: { reset: true } });
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  if (token === '') {
    return (
      <main className="centred">
        <div className="card form">
          <h1>Reset password</h1>
          <Notice kind="error">
            That link is incomplete. Please use the link from the email exactly as it was
            sent, or ask for a new one.
          </Notice>
          <p className="muted small">
            <Link to="/forgot-password">Request a new link</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="centred">
      <form className="card form" onSubmit={onSubmit}>
        <h1>Choose a new password</h1>

        <ErrorNotice error={error} />
        {mismatch ? <Notice kind="error">The two passwords do not match.</Notice> : null}

        <Field
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          autoFocus
          minLength={10}
          hint="At least 10 characters. Three or four unrelated words works well and is easy to remember."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Field
          label="New password again"
          type="password"
          name="confirmation"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
        />

        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save new password'}
        </button>
      </form>
    </main>
  );
}
